import { useEffect } from "react"
import { isModKey } from "@/lib/keyboard-platform"

export type KeyboardShortcut = {
  key: string
  /** Match Command (Mac) or Control (Windows/Linux). */
  modKey?: boolean
  /** If omitted, only matches when Control is not pressed (unless modKey is set). */
  ctrlKey?: boolean
  /** If omitted, only matches when Meta/Command is not pressed (unless modKey is set). */
  metaKey?: boolean
  shiftKey?: boolean
  /** If omitted, only matches when Alt/Option is not pressed (e.g. avoids Cmd+W matching Opt+Cmd+W). */
  altKey?: boolean
  /** Return false to leave the event for another handler (Escape in chat edit). */
  handler: ((e?: KeyboardEvent) => void | false) | (() => void | false)
  description?: string
}

function shortcutMatches(event: KeyboardEvent, shortcut: KeyboardShortcut): boolean {
  const keyMatches = event.key.toLowerCase() === shortcut.key.toLowerCase()
  if (!keyMatches) return false

  const shiftMatches =
    shortcut.shiftKey === undefined || shortcut.shiftKey === event.shiftKey
  const altMatches =
    shortcut.altKey === undefined ? !event.altKey : shortcut.altKey === event.altKey

  if (shortcut.modKey) {
    return isModKey(event) && shiftMatches && altMatches
  }

  const ctrlMatches =
    shortcut.ctrlKey === undefined ? !event.ctrlKey : shortcut.ctrlKey === event.ctrlKey
  const metaMatches =
    shortcut.metaKey === undefined ? !event.metaKey : shortcut.metaKey === event.metaKey

  return ctrlMatches && metaMatches && shiftMatches && altMatches
}

export function useKeyboardShortcuts(shortcuts: KeyboardShortcut[]) {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      for (const shortcut of shortcuts) {
        if (!shortcutMatches(event, shortcut)) continue
        // Do not steal Escape from open dialogs (image lightbox, confirm, etc.).
        if (
          shortcut.key.toLowerCase() === 'escape' &&
          document.querySelector('[data-slot="dialog-content"]')
        ) {
          continue
        }

        const handled =
          shortcut.handler.length > 0
            ? shortcut.handler(event as KeyboardEvent)
            : shortcut.handler()
        if (handled === false) continue
        event.preventDefault()
        event.stopPropagation()
        break
      }
    }

    window.addEventListener("keydown", handleKeyDown, true)
    return () => window.removeEventListener("keydown", handleKeyDown, true)
  }, [shortcuts])
}

export const KEYBOARD_SHORTCUTS = {
  TOGGLE_SIDEBAR: { key: "b", modKey: true, description: "Toggle sidebar" },
  TOGGLE_AGENT: { key: "j", modKey: true, description: "Toggle agent pane" },
  GLOBAL_SEARCH: { key: "k", modKey: true, description: "Global search" },
  COMMAND_PALETTE: { key: "p", modKey: true, description: "Command palette" },
  NEW_DOCUMENT: { key: "n", modKey: true, description: "New document" },
  SAVE_DOCUMENT: { key: "s", modKey: true, description: "Save document" },
  // Unbound on purpose - Cmd/Ctrl+Shift+R is browser hard-reload. Use the toolbar refresh control.
  REFRESH_DOCUMENT: { key: "", description: "Refresh document (toolbar only; shortcut reserved for browser)" },
  FOCUS_SIDEBAR: { key: "1", modKey: true, description: "Focus sidebar" },
  FOCUS_CENTER: { key: "2", modKey: true, description: "Focus center panel" },
  FOCUS_AGENT: { key: "3", modKey: true, description: "Focus agent pane" },
}
